import React, { useState } from 'react'
import WishOverlay from './wishOverlay';
import '../../i18n';
import { useTranslation } from 'react-i18next';
import { imageUrlToBase64 } from './helpers.js'
import dayjs from 'dayjs'
import SelflyImage from '../../images/selfly-logo.png'
import pdfModel from './pdf'
import pdfMake from "pdfmake/build/pdfmake";
import { sendConsumerResponse } from '../../api'


const PDF = async (products, purchaseDetails, companyLogoUrl, enableFooter, organizationDetails) => {
    const companyLogoBase64 = companyLogoUrl ? await imageUrlToBase64(companyLogoUrl) : undefined;
    const selflyLogoBase64 =
        enableFooter ?
            await imageUrlToBase64(SelflyImage, true)
            : undefined

    pdfMake.fonts = {
        Roboto: {
            normal: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Regular.ttf',
            bold: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Medium.ttf',
            italics: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Italic.ttf',
            bolditalics: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-MediumItalic.ttf'
        },
    }
    pdfMake.tableLayouts = {
        exampleLayout: {
            hLineWidth: function (i, node) {
                if (i === 0) {
                    return 0;
                }
                if (i === node.table.body.length) return 1;
                return (i === node.table.headerRows) ? 2 : 1;
            },
            vLineWidth: function (i) {
                return 0;
            },
            hLineColor: function (i) {
                return i === 1 ? 'black' : '#aaa';
            },
            paddingLeft: function (i) {
                return i === 0 ? 0 : 8;
            },
            paddingRight: function (i, node) {
                return (i === node.table.widths.length - 1) ? 0 : 8;
            },
            paddingTop: function (i) {
                return i === 0 ? 0 : 8;
            },
            paddingBottom: function (i, node) {
                return (i === node.table.widths.length - 1) ? 0 : 8;
            }
        }
    };
    pdfMake.createPdf(pdfModel(products, purchaseDetails, companyLogoBase64, selflyLogoBase64, organizationDetails))
        .download(`MySelfly_receipt_${dayjs().format('DD_MM_YYYY_HH_MM')}`);



}

/**
 * Prints out receipt table
 * @param {object} receipt Receipt object with purchases and total sum
 * @param {object} device Device used with purchase 
 * @param {number} mode Mode of consumer app. possible values are 1-4
 */
const ReceiptTable = ({ receipt, device, mode }) => {
    const { t } = useTranslation();

    const [overlayOpen, setOverlayOpen] = useState(false)

    const submitWishes = (wish) => {
        const params = new URLSearchParams(window.location.search)
        const deviceCode = params.get('deviceCode')
        const timestamp = params.get('t')
        const orderId = receipt.orderId;
        const wishObj = {
            wish,
            orderId,
            deviceCode,
            timestamp
        }
        sendConsumerResponse(wishObj)

    }

    let organizationDetails = receipt?.consumerAppSettings;

    // {"enableFeedback": true, "enableReceipt": true}
    const purchaseDetails = {
        totalSum: receipt?.purchases && receipt?.purchases?.totalSum,
        purchaseDate: dayjs(receipt?.timestamp).format("DD.MM.YYYY HH:mm"),
        orderId: receipt?.orderId,
        orderCabinet: device?.name,
        remainingSum: receipt?.remainingSum,
        paidAmount: receipt?.paidAmount,
        status: receipt?.status
    }

    const receiptOnly = mode === 4 || mode === 3;

    const generateProductPrice = (product) => {
        if (product.isDiscounted) {
            const discountedPrice = product.originalPrice ? product.originalPrice * (1.00 - product.discountAmount / 100) : product.price
            const originalPrice = product.originalPrice ? product.originalPrice : product.price * (1.00 + product.discountAmount / 100)
            return <>
                <td className="receipt__table-td">
                    <span className="receipt__table-td--strike">{`${product.currency} ${originalPrice.toFixed(2)}`}</span><br />
                    <span className="receipt__table-td--amount">{`${product.currency} ${product.discountAmountType && product.discountAmountType === 'fixed' ? product.price.toFixed(2) : discountedPrice.toFixed(2)}`}</span>
                </td>
                <td className="receipt__table-td">{`${product.currency} ${((product.discountAmountType && product.discountAmountType === 'fixed' ? product.price : discountedPrice) * product.count).toFixed(2)}`}</td></>
        }
        else {
            return <>
                <td className="receipt__table-td">
                    <span className="receipt__table-td">{`${product.currency} ${product.price.toFixed(2)}`}</span><br />
                </td>
                <td className="receipt__table-td">{`${product.currency} ${(product.price * product.count).toFixed(2)}`}</td></>
        }
    }
    let currency = '';
    try  {
        currency =  receipt?.purchases.purchase[0].currency || '';
    } catch(e) {}

    return (
        <>
            <div className="receipt animate__animated animate__fadeInRight">
                <table className="receipt__table">
                    <thead>
                        <tr>
                            <th className="receipt__table-header">{t('product')}</th>
                            <th className="receipt__table-header"> {t('quantityAbrv')}</th>
                            <th className="receipt__table-header"> {t('unitPrice')}</th>
                            <th className="receipt__table-header"> {t('total')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {receipt?.purchases?.purchase?.map((product, i) => {
                            return <tr key={`${product.id}-${i}`}>
                                <td className="receipt__table-td" >
                                    <div className="receipt__table-product">
                                        <img className="receipt__table-image" src={product.imageUrl} />
                                        <div className="receipt__table-text">{product.name}</div>
                                    </div>
                                </td>
                                <td className="receipt__table-td">{product.count}</td>
                                {generateProductPrice(product)}
                            </tr>

                        })}

                        {receipt.status === 'DONE' && 
                            <tr className="receipt__table-total">
                                <td colSpan="4">
                                    {t('totalCost')} : {`${currency} ${purchaseDetails?.totalSum?.toFixed(2) || 0} `}
                                </td>
                            </tr>
                        }
                        {receipt.status === 'PARTIAL' && 
                            <>
                                <tr className="receipt__table-total">
                                    <td colSpan="4">
                                        {t('paidAmount')} : {`${currency} ${purchaseDetails?.paidAmount?.toFixed(2)}`}
                                    </td>
                                </tr>
                                <tr className="receipt__table-total">
                                    <td colSpan="4">
                                        {t('remainingAmount')} : {`${currency} ${purchaseDetails?.remainingSum?.toFixed(2)}`}
                                    </td>
                                </tr>

                                <tr className="receipt__table-total">
                                    <td colSpan="4">
                                        {t('totalCost')} : {`${currency} ${purchaseDetails?.totalSum?.toFixed(2)}`}
                                    </td>
                                </tr>
                            </>
                        }
                        {(receipt?.status === 'DONE' || receipt?.status === 'PARTIAL') &&  receipt?.purchases && (
                            <tr className="receipt__table-total">
                                <td colSpan="4" className="receipt__table-total--download">
                                    <span onClick={() =>
                                        PDF(receipt?.purchases.purchase,
                                            purchaseDetails,
                                            organizationDetails?.organizationLogoUrl,
                                            organizationDetails?.enableSelflyStoreTrademark,
                                            organizationDetails)}
                                    >
                                        {t('downloadReceipt')}
                                    </span>
                                </td>
                            </tr>
                        )}
                        
                        {!receiptOnly &&
                            <tr className="receipt__table-total">
                                <td colSpan="4" className="receipt__table-total--download receipt__table-total--wish" onClick={() => { setOverlayOpen(true) }}>
                                    <span> {t('leaveWishes')} </span>
                                </td>
                            </tr>
                        }
                    </tbody>
                </table>
            </div>
            <WishOverlay submitWishes={submitWishes} open={overlayOpen} toggleOverlay={(value) => { setOverlayOpen(value) }} />
        </>

    )
}

export default ReceiptTable;
